<template>
    <MainContent
        title="Campos personalizados"
        :value="filters"
        :meta="meta"
        :loading="loading"
        @input="onFooterInput"
    >
        <!-- Header: Card con buscador + botón (skeleton o real) -->
        <template #header>
            <template v-if="!hasLoadedOnce && loading">
                <Card customClasses="tw:mb-4">
                    <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                        <template #skeleton>
                            <div class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-4 tw:justify-between">
                                <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:lg:max-w-xs tw:rounded-lg" />
                                <div class="tw:flex tw:flex-wrap tw:gap-2">
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-36 tw:rounded-lg" />
                                </div>
                            </div>
                        </template>
                    </Skeleton>
                </Card>
            </template>
            <template v-else>
                <Card customClasses="tw:mb-4">
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-4 tw:justify-between">
                        <div class="tw:w-full tw:sm:max-w-xs">
                            <SearchComponent
                                :value="filters.search"
                                @input="val => { filters.search = val; filters.page = 1 }"
                            />
                        </div>
                        <div class="tw:flex tw:flex-wrap tw:gap-2 tw:shrink-0">
                            <Button
                                data-cy="add-field"
                                icon="fas fa-plus"
                                @click="openAddModal"
                            >
                                Añadir campo
                            </Button>
                        </div>
                    </div>
                </Card>
            </template>
        </template>

        <FieldsList
            :fields="fields"
            :loading="loading"
            :columns="columns"
            empty-text="No hay campos personalizados"
            @delete="handleDelete"
        />

        <AddCampoModal
            name="add-fields"
            :is-bases="isBases"
            :available-fields="availableFields"
            :loading="availableFieldsLoading"
            @add="handleAdd"
        />
    </MainContent>
</template>

<script setup>
    import { ref, watch } from 'vue';
    import { useModal } from '@/composables/common/useModal';

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import FieldsList from '@/modules/admin/campos-personalizados/components/FieldsList.vue';
    import AddCampoModal from '@/modules/admin/campos-personalizados/components/AddCampoModal.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    import { useCamposPersonalizados } from '@/composables/admin/campos-personalizados/useCamposPersonalizados';

    const props = defineProps({
        context: {
            type: String,
            default: 'bases',
            validator: value => ['bases', 'eventos', 'abonos'].includes(value)
        },
        entityId: {
            type: Number,
            required: true
        }
    });

    const modal = useModal();

    const {
        loading,
        fields,
        meta,
        filters,
        columns,
        isBases,
        availableFields,
        availableFieldsLoading,
        addFields,
        deleteField
    } = useCamposPersonalizados(props.context, props.entityId);

    const hasLoadedOnce = ref(false);
    watch(fields, val => {
        if (val?.length) hasLoadedOnce.value = true;
    });

    function openAddModal() {
        modal.show('add-fields');
    }

    /**
     * El pie de tabla emite un objeto NUEVO con `page`/`per_page` ya
     * actualizados. El handler vive en el script a propósito: dentro del
     * template los refs llegan desenvueltos, así que `filters.value` era
     * `undefined` y `Object.assign` lanzaba
     * `TypeError: Cannot convert undefined or null to object`. Al reventar
     * antes de mutar, el watcher de la lista no veía el cambio de página y no
     * se disparaba ninguna petición.
     *
     * @param {Object} nextFilters objeto completo del pie ({ ...filters })
     */
    function onFooterInput(nextFilters) {
        Object.assign(filters.value, nextFilters);
    }

    function handleAdd(selectedFields) {
        addFields(selectedFields);
    }

    async function handleDelete(fieldId) {
        try {
            await deleteField(fieldId);
        } catch {
            // Error handled in composable
        }
    }
</script>
